import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FaGlobe } from 'react-icons/fa';
import config from '../../../config/config';
import './LanguageSwitcher.css';

interface Language {
  code: string;
  name: string;
  flag: string;
  rtl?: boolean;
}

const LanguageSwitcher: React.FC = () => {
  const { i18n } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // i18n.language can carry a region subtag ("fr-FR"), so compare on the base code
  const activeCode = (i18n.language || config.defaultLanguage).split('-')[0];

  // Find the current language
  const currentLanguage = config.languages.find((lang: Language) => lang.code === activeCode) || config.languages[0];

  const changeLanguage = (languageCode: string): void => {
    i18n.changeLanguage(languageCode);
    setIsOpen(false);
    
    // If the language is RTL, set the dir attribute on the html element
    const isRtl = config.languages.find((lang: Language) => lang.code === languageCode)?.rtl;
    document.documentElement.dir = isRtl ? 'rtl' : 'ltr';
    
    // Store the selected language in localStorage
    localStorage.setItem('i18nextLng', languageCode);
  };
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  const isRtl = currentLanguage.rtl;

  return (
    <div className="language-container" ref={dropdownRef}>
      <button
        className="language-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Change language"
        aria-expanded={isOpen}
        title={currentLanguage.name}
      >
        <FaGlobe aria-hidden="true" />
        <span>{currentLanguage.code.toUpperCase()}</span>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className={`language-options-row ${isRtl ? 'rtl' : 'ltr'}`}
            initial={{ opacity: 0, x: isRtl ? -10 : 10 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isRtl ? -10 : 10 }}
            transition={{ duration: 0.2 }}
          >
            {config.languages.map((language: Language) => (
              <button
                key={language.code}
                onClick={() => changeLanguage(language.code)}
                className={`language-option-icon ${language.code === activeCode ? 'active' : ''}`}
                aria-label={`Change language to ${language.name}`}
                title={language.name}
              >
                <span>{language.code.toUpperCase()}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSwitcher;
